import { modals } from '@mantine/modals'; // Ensure the import path is correct
import { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { usePushNotifications } from 'src/Hooks/Notifications/usePushNotifications';

// To avoid asking for permission on every visit
const PUSH_PROMPT_KEY = 'pushPermissionLastPrompted';
const PROMPT_DELAY_MS = 3000; // Delay before showing the modal (3 seconds)
const MIN_DAYS_BETWEEN_PROMPTS = 7; // Do not ask again for 7 days

export const PushNotificationProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const { permission, isSubscribed, requestPermissionAndSubscribe } = usePushNotifications();

  useEffect(() => {
    // 1. Initial conditions: browser must be compatible,
    //    user must not have already granted/denied permission,
    //    and they must not already be subscribed.
    if (permission !== 'default' || isSubscribed || !('PushManager' in window)) {
      console.log(permission, isSubscribed, 'PushManager' in window);
      return;
    }

    // 2. Frequency check: have we prompted recently?
    const lastPromptedTimestamp = localStorage.getItem(PUSH_PROMPT_KEY);
    if (lastPromptedTimestamp) {
      const lastPromptDate = new Date(Number(lastPromptedTimestamp));
      const now = new Date();
      const daysSinceLastPrompt = (now.getTime() - lastPromptDate.getTime()) / (1000 * 3600 * 24);
      if (daysSinceLastPrompt < MIN_DAYS_BETWEEN_PROMPTS) {
        return; // It's too soon, do nothing.
      }
    }

    // 3. If all conditions are met, show the modal after a short delay.
    const timer = setTimeout(() => {
      modals.openContextModal({
        modal: 'info',
        title: t('pushNotifications.prompt.title', 'Restez informé !'),
        centered: true,
        innerProps: {
          content: t(
            'pushNotifications.prompt.content',
            'Activez les notifications pour ne rien manquer des actualités importantes concernant vos biens.'
          ),
          confirmText: t('pushNotifications.prompt.accept', 'Activer'),
          cancelText: t('pushNotifications.prompt.decline', 'Plus tard'),
          onConfirm: () => {
            requestPermissionAndSubscribe();
            localStorage.setItem(PUSH_PROMPT_KEY, Date.now().toString());
          },
          onCancel: () => {
            localStorage.setItem(PUSH_PROMPT_KEY, Date.now().toString());
          },
        },
      });
    }, PROMPT_DELAY_MS);

    return () => clearTimeout(timer);
  }, [permission, isSubscribed, requestPermissionAndSubscribe, t]);

  return <>{children}</>;
};

export default PushNotificationProvider;
